import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { SearchInput } from '@liveauctioneers/hammer-ui-core/searchInput';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { colorBackgroundPrimary } from '@liveauctioneers/hammer-ui-theme/vars';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useDebouncedAuctionHouseTypeahead } from '@/pages/Search/SearchPage/hooks/useDebouncedAuctionHouseTypeahead';
import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { useServerSideFacetExpansion } from '@/pages/Search/SearchPage/hooks/useServerSideFacetExpansion';
import {
    AUCTION_HOUSE_VIEW_MORE_SIZE,
    CurrentOpenMenu,
    VIEW_MORE_CUTOFF_HEIGHT,
} from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getAuctionHouseTypeaheadStatus } from '@/redux/modules/search/search.selectors';
import { ElasticAggregateDemandStatus } from '@/redux/modules/search/search.types';
import { getAuctionHouseTypeaheadOptions, getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';

// Intentionally inlines Accordion + ViewMore instead of delegating to
// FacetCard. AuctionHouseFilter needs a SearchInput between the Accordion
// headline and the ViewMore body for the typeahead branch; FacetCard's
// shape (Accordion → ViewMore → children) has no slot for that. Keeps the
// scroll-reset + loading-spinner pattern in sync with FacetCard manually --
// if either is changed there, mirror it here.
export const AuctionHouseFilter = () => {
    const { formatMessage } = useIntl();
    const debouncedTypeaheadFetch = useDebouncedAuctionHouseTypeahead();
    const { isLoading: viewMoreIsLoading, onViewMoreClick } = useServerSideFacetExpansion({
        facetId: FacetTypes.AUCTION_HOUSE_ID,
        size: AUCTION_HOUSE_VIEW_MORE_SIZE,
    });

    const [auctionHouseSearchText, setAuctionHouseSearchText] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleViewMoreChange = useCallback((expanded: boolean) => {
        setIsExpanded(expanded);
        if (!expanded && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, []);

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();

    const auctionHouseFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.AUCTION_HOUSE_ID));
    const { options: defaultOptions = [] } = auctionHouseFacet;
    const typeaheadOptions = useAppSelector(getAuctionHouseTypeaheadOptions);
    const typeaheadStatus = useAppSelector(getAuctionHouseTypeaheadStatus);

    const trimmedSearchText = auctionHouseSearchText.trim();
    const isTypeaheadActive = trimmedSearchText !== '';
    const typeaheadIsLoading = isTypeaheadActive && typeaheadStatus === ElasticAggregateDemandStatus.Loading;

    // Reset the local expand mirror on URL change so the inner overflow-y-auto
    // class re-applies cleanly after a filter is applied.
    useEffect(() => {
        setIsExpanded(false);
    }, [search]);

    const onAuctionHouseOptionClick = useCallback(
        (option: FacetOption) => {
            const newCheckStatus = !option.selected;
            let newParams = modifyParamsWhenChecked(
                queryParams,
                FacetTypes.AUCTION_HOUSE_ID,
                option.id,
                newCheckStatus
            );
            newParams = modifyParamsRemovePage(newParams);

            navigateWithParams({
                currentOpenMenu: CurrentOpenMenu.AllFilters,
                filterAction: newCheckStatus ? FilterAction.AuctionHouseChanged : FilterAction.AuctionHouseRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [queryParams, navigateWithParams]
    );

    const onSearchTextChange = useCallback(
        (searchText: string) => {
            setAuctionHouseSearchText(searchText);
            // Whitespace-only input is a no-op on the server side -- cancel any
            // pending debounce so we don't fire a stale request when the user
            // pauses mid-clear (e.g. typing then deleting back to whitespace).
            if (searchText.trim() === '') {
                debouncedTypeaheadFetch.cancel();
                return;
            }
            debouncedTypeaheadFetch(searchText);
        },
        [debouncedTypeaheadFetch]
    );

    const onClear = useCallback(() => {
        // Kill any pending debounce so the X icon truly resets typeahead state
        // -- without this, a stale request can fire ~300ms after clear.
        debouncedTypeaheadFetch.cancel();
        setAuctionHouseSearchText('');
    }, [debouncedTypeaheadFetch]);

    const visibleOptions = useMemo(() => {
        if (isTypeaheadActive) {
            return typeaheadOptions;
        }
        return defaultOptions
            .filter((option) => option.count !== 0 || option.selected)
            .sort((firstOption, secondOption) => secondOption.count - firstOption.count);
    }, [isTypeaheadActive, typeaheadOptions, defaultOptions]);

    // Render-or-hide gate matches the pre-rewrite behavior: keep the section
    // out of the drawer entirely when there's no data AND the user hasn't
    // started typing. Once typeahead is active the section stays visible so
    // the "no results found" message can land.
    if (visibleOptions.length === 0 && !isTypeaheadActive) {
        return null;
    }

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'search_facet_title_auctionHouse' })}
            open
        >
            <div className="flex flex-col gap-xs">
                <SearchInput
                    className="mb-xs"
                    onClear={onClear}
                    onTextChange={onSearchTextChange}
                    placeholder={formatMessage({ id: 'searchPage.facets.filterActionHouses' })}
                    small
                    value={auctionHouseSearchText}
                />
                {typeaheadIsLoading ? (
                    <div className="flex justify-center py-xs">
                        <Loading />
                    </div>
                ) : visibleOptions.length === 0 ? (
                    <H5>{formatMessage({ id: 'searchPage.facets.noResultsFound' })}</H5>
                ) : isTypeaheadActive ? (
                    // Typeahead is already a server-side expansion -- render its
                    // results directly without ViewMore truncation.
                    <div className="flex max-h-[400px] flex-col gap-xs overflow-y-auto py-xs">
                        {visibleOptions.map((option) => (
                            <Checkbox.Option
                                checked={option.selected}
                                disabled={option.count === 0 && !option.selected}
                                key={option.id}
                                onChange={() => onAuctionHouseOptionClick(option)}
                            >
                                {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                            </Checkbox.Option>
                        ))}
                    </div>
                ) : (
                    <ViewMore
                        collapseText={formatMessage({ id: 'view_less' })}
                        expandText={formatMessage({ id: 'view_more' })}
                        fadeBackgroundColor={`rgb(var(${colorBackgroundPrimary}))`}
                        key={search}
                        onChange={handleViewMoreChange}
                        onClick={onViewMoreClick}
                        textCutoffHeight={VIEW_MORE_CUTOFF_HEIGHT}
                    >
                        <div
                            className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden py-xs', {
                                'overflow-y-auto': isExpanded,
                            })}
                            ref={scrollContainerRef}
                        >
                            {visibleOptions.map((option) => (
                                <Checkbox.Option
                                    checked={option.selected}
                                    disabled={option.count === 0 && !option.selected}
                                    key={option.id}
                                    onChange={() => onAuctionHouseOptionClick(option)}
                                >
                                    {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                                </Checkbox.Option>
                            ))}
                            {isExpanded && viewMoreIsLoading && (
                                <div className="flex justify-center py-xs">
                                    <Loading />
                                </div>
                            )}
                        </div>
                    </ViewMore>
                )}
            </div>
        </Accordion>
    );
};
